import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Clock, 
  CreditCard, 
  Check, 
  AlertTriangle,
  Loader2,
  Shield,
  Zap,
  Smartphone,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { initializePaddle } from '@paddle/paddle-js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paddle configuration - will be set from backend
let paddleInstance = null;

export default function Paywall({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showPaywall, setShowPaywall] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paddleReady, setPaddleReady] = useState(false);

  // Initialize Paddle
  useEffect(() => {
    const initPaddle = async () => {
      try {
        // Get Paddle config from backend
        const response = await axios.get(`${API}/subscription/paddle-config`);
        const { client_token, environment } = response.data;
        
        if (client_token) {
          paddleInstance = await initializePaddle({
            token: client_token,
            environment: environment || 'sandbox',
          });
          setPaddleReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize Paddle:', error);
        // Fallback to direct checkout URLs if Paddle fails to init
      }
    };
    
    initPaddle();
  }, []);

  useEffect(() => {
    if (user) {
      checkSubscription();
    }
  }, [user]);

  const checkSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
      
      // Show paywall if trial expired and not paid
      if (response.data.trial_expired && !response.data.has_paid) {
        setShowPaywall(true);
      }
    } catch (error) {
      console.error('Failed to check subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (planType) => {
    setSelectedPlan(planType);
    setProcessingPayment(true);
    
    try {
      // Create checkout session with plan type
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        plan_type: planType
      });
      
      const { checkout_url, price_id } = response.data;
      
      // Try Paddle overlay checkout first
      if (paddleReady && paddleInstance && price_id) {
        paddleInstance.Checkout.open({
          items: [{ priceId: price_id, quantity: 1 }],
          customer: { email: user?.email || '' },
          settings: {
            displayMode: 'overlay',
            locale: 'en',
            theme: 'light',
            successUrl: `${window.location.origin}/payment/success`,
          },
        });
        setProcessingPayment(false);
        setSelectedPlan(null);
      } else if (checkout_url) {
        // Fallback to redirect
        window.location.href = checkout_url;
      } else {
        throw new Error('No checkout URL available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If paid or trial active, render children
  if (subscription?.has_paid || subscription?.trial_active) {
    return (
      <>
        {children}
        {/* Trial banner */}
        {subscription?.trial_active && !subscription?.has_paid && (
          <TrialBanner 
            daysLeft={subscription.trial_days_left} 
            onUpgrade={() => setShowPaywall(true)} 
          />
        )}
        {/* Payment dialog - can be opened from trial banner */}
        <PaymentDialog 
          open={showPaywall} 
          onOpenChange={setShowPaywall}
          processingPayment={processingPayment}
          selectedPlan={selectedPlan}
          onPurchase={handlePurchase}
        />
      </>
    );
  }

  // Show paywall
  return (
    <PaymentDialog 
      open={showPaywall} 
      onOpenChange={() => {}}
      processingPayment={processingPayment}
      selectedPlan={selectedPlan}
      onPurchase={handlePurchase}
      hideClose={true}
    />
  );
}

function PaymentDialog({ open, onOpenChange, processingPayment, selectedPlan, onPurchase, hideClose }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" hideClose={hideClose}>
        <DialogHeader className="text-center">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Star className="h-8 w-8 text-white" />
          </div>
          <DialogTitle className="font-heading text-2xl">
            Upgrade to QA Builder Pro
          </DialogTitle>
          <DialogDescription>
            Choose a plan to continue using QA Builder Pro.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {/* Monthly Plan */}
          <Card className="border-2 hover:border-primary/50 transition-colors">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold">£3</CardTitle>
                  <CardDescription>per month</CardDescription>
                </div>
                <Badge variant="outline">Monthly</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Feature icon={Check} text="Unlimited QA forms" />
              <Feature icon={Check} text="All trade templates" />
              <Feature icon={Check} text="Photo uploads & signatures" />
              <Feature icon={Check} text="PDF export & email" />
              <Feature icon={Check} text="Offline mode with sync" />
              <Feature icon={Check} text="Cancel anytime" />
              
              <Button 
                onClick={() => onPurchase('monthly')}
                disabled={processingPayment}
                variant="outline"
                className="w-full mt-4 h-11"
                data-testid="monthly-btn"
              >
                {processingPayment && selectedPlan === 'monthly' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscribe £3/month
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Lifetime Plan */}
          <Card className="border-2 border-primary relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-primary text-white">Best Value</Badge>
            </div>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold">£10</CardTitle>
                  <CardDescription>one-time payment</CardDescription>
                </div>
                <Badge className="bg-green-100 text-green-700">Lifetime</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Feature icon={Check} text="Unlimited QA forms" />
              <Feature icon={Check} text="All trade templates" />
              <Feature icon={Check} text="Photo uploads & signatures" />
              <Feature icon={Check} text="PDF export & email" />
              <Feature icon={Check} text="Offline mode with sync" />
              <Feature icon={Star} text="Never pay again!" highlight />
              
              <Button 
                onClick={() => onPurchase('lifetime')}
                disabled={processingPayment}
                className="w-full mt-4 h-11 bg-primary hover:bg-primary/90"
                data-testid="lifetime-btn"
              >
                {processingPayment && selectedPlan === 'lifetime' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay £10 - Lifetime Access
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-4 space-y-2">
          <p className="text-xs text-muted-foreground">
            Secure payment powered by Paddle. Monthly subscriptions can be cancelled anytime.
          </p>
          <div className="flex justify-center gap-4 text-xs">
            <Link to="/terms" className="text-primary hover:underline">Terms</Link>
            <Link to="/privacy" className="text-primary hover:underline">Privacy</Link>
            <Link to="/refund" className="text-primary hover:underline">Refund Policy</Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Feature({ icon: Icon, text, highlight }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-amber-100' : 'bg-green-100'}`}>
        <Icon className={`h-3 w-3 ${highlight ? 'text-amber-600' : 'text-green-600'}`} />
      </div>
      <span className={`text-sm ${highlight ? 'font-medium text-amber-700' : ''}`}>{text}</span>
    </div>
  );
}

export function TrialBanner({ daysLeft, onUpgrade }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 max-w-sm">
      <Card className="border-amber-200 bg-amber-50 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800">
                {daysLeft > 0 
                  ? `Trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`
                  : 'Trial ending soon!'
                }
              </p>
              <p className="text-sm text-amber-700 mt-1">
                From £3/month or £10 lifetime access
              </p>
              <div className="flex gap-2 mt-3">
                <Button 
                  size="sm" 
                  onClick={onUpgrade}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Zap className="h-4 w-4 mr-1" />
                  Upgrade
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setDismissed(true)}
                  className="text-amber-700"
                >
                  Later
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
